import {
  TableCell,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { Cookies, isNoteEditable } from "utils";
import {
  forwardRef,
  MouseEvent,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  Fragment,
  useContext,
  useEffect,
} from "react";
import { useDropzone, FileError } from "react-dropzone";
import { FIRST_NAME_COOKIE, LAST_NAME_COOKIE } from "constant";
import moment from "moment";
import { AttachFile, DeleteOutlined, GetApp } from "@mui/icons-material";
import { useAxios } from "myAxios";
import { Note, NoteFile } from "types/summary";
import { toast } from "react-toastify";
import ArrowPopper from "./ArrowPopper";
import MButton from "components/momentum/MButton";
import { AuthProvider } from "pages";
import { AxiosResponse } from "axios";
import { v4 as uuidv4 } from "uuid";

const useStyle = makeStyles(() => ({
  dropzone: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 20,
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
  },
  container: { marginTop: 8, maxHeight: "27vh", overflowY: "auto" },
  p: {
    cursor: "pointer",
  },
  rejectTitle: {
    fontWeight: 400,
    fontSize: 15,
    marginTop: 8,
    backgroundColor: "#F2F4F5",
    padding: "12px 7px",
  },
}));

const StyledTableCell = withStyles({
  head: {
    backgroundColor: "#F2F4F5",
    fontWeight: 400,
    fontSize: 15,
    padding: "12px 7px",
    borderBottom: "none",
    "&:first-of-type": {
      borderLeft: "1px solid #F2F4F5",
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
    },
    "&:last-of-type": {
      borderRight: "1px solid #F2F4F5",
      borderTopRightRadius: 4,
      borderbottomRightRadis: 4,
    },
  },
  body: {
    fontWeight: 400,
    fontSize: 14,
    padding: 8,
    "&:first-of-type": {
      borderLeft: "1px solid #E6E8E8",
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      maxWidth: 300,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "&:last-of-type": {
      borderRight: "1px solid #E6E8E8",
      borderTopRightRadius: 4,
      borderbottomRightRadis: 4,
    },
  },
})(TableCell);

// 30MB
export const MAX_FILE_SIZE = 30 * 1024 * 1024;
export const MAX_FILE_NAME_LENGTH = 255;

export interface FileUploaderProps {
  note?: Partial<Note>;
  onSubmitCallback?: (ids?: string[]) => void;
  onFileDeleteCallback?: () => void;
  // this is to fix if pass id, mutate will take as the second arg and cause issue
  onFileDeleteCallbackWithId?: (id: string) => void;
  /**
   * hide upload to display uploaded files only
   */
  displayUploadedOnly?: boolean;
  disableEditableCheck?: boolean;
  submitURL: string;
  deleteURL?: string;
  isEditable?: boolean;
  batch?: boolean;
  /**
   * when true, delete will not call the API
   */
  dryrun?: boolean;
  disabled?: boolean;
  pageId?: string;
}

export interface FileUploaderRef {
  submit: (submitProps?: SubmitProps) => Promise<void | string[]>;
  getFiles: () => File[];
  getUploadedFiles: () => NoteFile[];
}

export type SubmitProps = { [key: string]: string };

const FileUploader = forwardRef<FileUploaderRef, FileUploaderProps>(
  (
    {
      note,
      onSubmitCallback,
      onFileDeleteCallback,
      onFileDeleteCallbackWithId,
      submitURL,
      isEditable,
      displayUploadedOnly = false,
      disableEditableCheck = false,
      deleteURL,
      batch = false,
      dryrun = false,
      disabled = false,
      pageId = "renewal",
    },
    ref
  ) => {
    const classes = useStyle();
    const [files, setFiles] = useState<File[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<NoteFile[]>([]);
    const axios = useAxios();
    const submitAxios = useAxios(undefined, "dataService", false);
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    const [deleteId, setDeleteId] = useState("");

    const [isUploading, setIsUploading] = useState(false);

    const [progressMap, setProgressMap] = useState<Record<string, number>>({});

    useEffect(() => {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (isUploading) {
          e.preventDefault();
          e.returnValue = "";
        }
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isUploading]);

    useEffect(() => {
      if (note?.files) {
        setUploadedFiles(note.files);
      }
      if (note?.attachments) {
        setFiles(note.attachments);
      }
    }, [note]);

    useEffect(() => {
      // console.log("xxx", progressMap);
    }, [progressMap]);

    const authDetail = useContext(AuthProvider);

    const editable = disableEditableCheck
      ? true
      : isEditable !== undefined
        ? isEditable
        : isNoteEditable(authDetail, note);

    const onClick = (id: string) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setDeleteId(id);
      setAnchor(anchor ? null : e.currentTarget);
    };

    const onCancel = () => {
      setDeleteId("");
      setAnchor(null);
    };

    useImperativeHandle(ref, () => ({
      submit: (submitProps?: SubmitProps) => submitHandler(submitProps),
      getFiles: () => files,
      getUploadedFiles: () => uploadedFiles,
    }));

    const userName = useMemo(
      () => Cookies.get(FIRST_NAME_COOKIE) + " " + Cookies.get(LAST_NAME_COOKIE),
      []
    );
    const now = moment().format("MM/DD/YYYY");

    const onDrop = useCallback((acceptedFiles: File[]) => {
      setFiles((prv) => {
        if (
          prv.reduce((prv, cur) => prv + cur.size, 0) +
            acceptedFiles.reduce((prv, cur) => prv + cur.size, 0) >
          MAX_FILE_SIZE
        ) {
          toast.error("Maxium size for one batch is 30MB", {
            position: "top-center",
            autoClose: 3000,
          });
          return prv;
        }
        return [...prv, ...acceptedFiles];
      });
    }, []);

    const validator: <T extends File>(file: T) => FileError | FileError[] | null = useCallback(
      (file: File) => {
        if (file.size > MAX_FILE_SIZE) {
          return { code: "file-too-large", message: "Max file size 30MB" };
        } else if (file.name.length > MAX_FILE_NAME_LENGTH) {
          return {
            code: "file-name-too-long",
            message: "Max file name length 255",
          };
        }
        return null;
      },
      []
    );

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
      onDrop,
      validator,
    });

    const replaceURL = (url: string, submitProps: SubmitProps) =>
      url.replace(/\{(.+?)\}/g, function (_s, key) {
        return submitProps[key];
      });

    const submitHandler = (submitProps?: SubmitProps) =>
      submitProps && files.length
        ? new Promise<string[]>((resolve, reject) => {
            /**
             * @description this works online, need to set local proxy when developing
             */
            const url = replaceURL(submitURL, submitProps);
            if (batch) {
              setIsUploading(true);
              Promise.all<AxiosResponse<string>>(
                files.map(async (f) => {
                  const postData = new FormData();
                  postData.append("file", f);
                  (f as any).tmpid = uuidv4();
                  return submitAxios.post<string>(url, postData, {
                    headers: {
                      "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: (ev) => {
                      const percent = Math.round((ev.loaded * 100) / (ev.total || 1));
                      setProgressMap((prev) => ({
                        ...prev,
                        [(f as any).tmpid]: percent,
                      }));
                    },
                  });
                })
              )
                .then((res) => {
                  onSubmitCallback?.();
                  resolve(res.map((x) => x.data));
                  setFiles([]);
                  setIsUploading(false);
                  toast.info("Files uploaded successfully.");
                })
                .catch((e) => {
                  reject(e);
                  toast.warning("File upload failed.");
                });
            } else {
              const postData = new FormData();
              files.forEach((f) => {
                // (f as any).tmpid=uuidv4()
                postData.append("files", f);
              });
              setIsUploading(true);
              submitAxios
                .post(url, postData, {
                  headers: {
                    "Content-Type": "multipart/form-data",
                  },
                  onUploadProgress: (ev) => {
                    const percent = Math.round((ev.loaded * 100) / (ev.total || 1));
                    setProgressMap((prev) => ({
                      ...prev,
                      ["allinone"]: percent,
                    }));
                  },
                })
                .then((res) => {
                  onSubmitCallback?.();
                  resolve(res.data?.successIds || []);
                  setFiles([]);
                  setIsUploading(false);
                  toast.info("Files uploaded successfully.");
                })
                .catch((e) => {
                  reject(e);
                  toast.warning("File upload failed.");
                });
            }
          })
        : new Promise<void>((resolve) => {
            onSubmitCallback?.();
            resolve();
          });

    const onDelete = (name: string) => () => {
      setFiles((prv) => prv.filter((p) => p.name !== name));
    };

    const onDeleteUploadedFile = () => () => {
      setAnchor(null);
      if (dryrun) {
        setUploadedFiles((prv) => prv?.filter((x) => x.fileId !== deleteId));
        onFileDeleteCallback?.();
        onFileDeleteCallbackWithId?.(deleteId);
        return;
      }
      const url = deleteURL
        ? `${replaceURL(deleteURL, {
            fileId: deleteId,
            ...note,
          } as any)}`
        : `/api/dataservice/v1/files/${deleteId}`;
      axios
        .delete(url, {
          data: [deleteId],
        })
        .then(() => {
          setUploadedFiles((prv) => prv?.filter((x) => x.fileId !== deleteId));
          onFileDeleteCallback?.();
          onFileDeleteCallbackWithId?.(deleteId);
        })
        .catch((e) => {
          toast.error(e?.response?.data?.message || "Fail to delete file", {
            position: "top-center",
            autoClose: 3000,
          });
        });
    };

    return (
      <section
        style={{
          position: "relative",
          pointerEvents: disabled ? "none" : undefined,
        }}
      >
        <ArrowPopper
          open={Boolean(anchor)}
          anchorEl={anchor}
          placement="left"
          handleClose={onCancel}
          disableFlip={true}
          disablePortal={true}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <p
              style={{
                justifySelf: "center",
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              Are you sure to delete this file?
            </p>
            <div style={{ alignSelf: "flex-end", marginTop: 8 }}>
              <MButton
                buttonType="orange"
                buttonSize={28}
                onClick={onCancel}
              >
                Cancel
              </MButton>
              <MButton
                buttonType="blue"
                buttonSize={28}
                onClick={onDeleteUploadedFile()}
              >
                Submit
              </MButton>
            </div>
          </div>
        </ArrowPopper>
        {displayUploadedOnly ? null : (
          <div {...getRootProps({ className: classes.dropzone })}>
            <input {...getInputProps()} />
            <p className={classes.p}>
              {isDragActive
                ? "Drop the files here ..."
                : "Drag & Drop some files here, or click to select files"}
            </p>
          </div>
        )}
        {uploadedFiles?.length || files.length ? (
          <TableContainer className={classes.container}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell align="left">Document</StyledTableCell>
                  <StyledTableCell align="left">Uploaded By</StyledTableCell>
                  <StyledTableCell align="left">Upload Date</StyledTableCell>
                  {!displayUploadedOnly && <StyledTableCell align="left">Actions</StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadedFiles?.map((f) => (
                  <Fragment key={f.fileId}>
                    <TableRow key={f.fileName}>
                      <StyledTableCell align="left">
                        {pageId === "detail" ? (
                          <a
                            style={{
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              cursor: "default",
                              textDecoration: "none",
                            }}
                          >
                            <AttachFile
                              fontSize="small"
                              style={{ rotate: "45deg" }}
                            />
                            <p
                              style={{
                                maxWidth: 160,
                              }}
                            >
                              {f.fileName}
                            </p>
                          </a>
                        ) : (
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href={`/api/dataservice-blob/v1/files/${f.fileId}`}
                            style={{
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <GetApp />
                            <p
                              style={{
                                maxWidth: 160,
                              }}
                            >
                              {f.fileName}
                            </p>
                          </a>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="left">{f.uploadedBy}</StyledTableCell>
                      <StyledTableCell align="left">
                        {moment(f.uploadedTime).format("MM/DD/YYYY")}
                      </StyledTableCell>
                      {!displayUploadedOnly && (
                        <StyledTableCell
                          align="left"
                          style={{ display: "flex" }}
                        >
                          {editable && f.uploadedBy !== "SYSTEM" && (
                            <IconButton
                              size="small"
                              onClick={onClick(f.fileId)}
                            >
                              <DeleteOutlined color="error" />
                            </IconButton>
                          )}
                          {pageId === "detail" ? (
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={`/api/dataservice-blob/v1/files/${f.fileId}`}
                              style={{
                                fontSize: 14,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <GetApp />
                            </a>
                          ) : null}
                        </StyledTableCell>
                      )}
                    </TableRow>
                  </Fragment>
                ))}
                {files.map((f) => (
                  <TableRow key={f.name}>
                    <StyledTableCell align="left">{f.name}</StyledTableCell>
                    <StyledTableCell align="left">{userName}</StyledTableCell>
                    <StyledTableCell align="left">
                      {now}
                      {progressMap &&
                      (progressMap[(f as any).tmpid] < 100 ||
                        (progressMap["allinone"] > 0 && progressMap["allinone"] < 100)) ? (
                        <div>
                          <img
                            src="/circle.svg"
                            width="30"
                            height="30"
                            alt="uploading"
                          />
                        </div>
                      ) : null}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      <IconButton
                        size="small"
                        onClick={onDelete(f.name)}
                      >
                        <DeleteOutlined color="error" />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
        {fileRejections.length ? (
          <div>
            <div className={classes.rejectTitle}>Rejected Files</div>
            {fileRejections.map((f) => (
              <div key={f.file.name}>
                {f.file.name} - {(f.file.size / 1024 / 1024).toFixed(0)} (MB)
                <ul>
                  {f.errors.map((e) => (
                    <li key={e.code}>{e.message}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}
      </section>
    );
  }
);

export default FileUploader;
